import { useMemo } from 'react';

import { useTenantUsageMetricsContext } from '../contexts/TenantUsageMetricsContext';
import type {
  AuthBootstrapSubscriptionSummary,
  TenantUsageSummaryEvent,
} from '../types';

interface UseTenantUsageMetricsOptions {
  enabled?: boolean;
  initialAiSubscription?: AuthBootstrapSubscriptionSummary | null;
  initialReverseIvrSubscription?: AuthBootstrapSubscriptionSummary | null;
}

interface UseTenantUsageMetricsReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  aiSubscription: AuthBootstrapSubscriptionSummary | null;
  reverseIvrSubscription: AuthBootstrapSubscriptionSummary | null;
  lastEvent: TenantUsageSummaryEvent | null;
  refresh: (reason?: string) => Promise<void>;
}

/**
 * Read the shared tenant usage metrics store.
 */
export function useTenantUsageMetrics(
  options: UseTenantUsageMetricsOptions = {},
): UseTenantUsageMetricsReturn {
  const {
    enabled = true,
    initialAiSubscription = null,
    initialReverseIvrSubscription = null,
  } = options;
  const context = useTenantUsageMetricsContext();

  return useMemo(
    () => ({
      isConnected: context.isConnected,
      isSubscribed: enabled ? context.isSubscribed : false,
      isRefreshing: enabled ? context.isRefreshing : false,
      aiSubscription: enabled ? (context.aiSubscription ?? initialAiSubscription) : initialAiSubscription,
      reverseIvrSubscription: enabled
        ? (context.reverseIvrSubscription ?? initialReverseIvrSubscription)
        : initialReverseIvrSubscription,
      lastEvent: enabled ? context.lastPayload : null,
      refresh: context.refresh,
    }),
    [
      context.aiSubscription,
      context.isConnected,
      context.isRefreshing,
      context.isSubscribed,
      context.lastPayload,
      context.refresh,
      context.reverseIvrSubscription,
      enabled,
      initialAiSubscription,
      initialReverseIvrSubscription,
    ],
  );
}

export default useTenantUsageMetrics;
